import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload,
  Users,
  Banknote,
  Tag,
  Shield,
  Megaphone,
  MessageSquare,
  BarChart2,
  FileText,
  Zap,
  CreditCard,
  Warehouse,
  Ship,
  MapPin,
  Building2,
  Truck,
  CheckCircle,
  Package,
} from 'lucide-react';
import { useAuth } from '../../auth/context/AuthContext';
import ThemeToggle from '../../../app/components/ThemeToggle';
import ActivePromotionsBanner from '../../shared/ActivePromotionsBanner';

const CARDS = [
  { icon: Upload, label: 'Upload Sheet', sub: 'Import warehouse manifest', to: '/admin/upload' },
  { icon: Users, label: 'Users', sub: 'Manage customer accounts', to: '/admin/users' },
  { icon: Banknote, label: 'Transactions', sub: 'Payments and verification', to: '/admin/transactions' },
  { icon: Tag, label: 'Pricing', sub: 'Sea and air rates', to: '/admin/pricing' },
  { icon: Building2, label: 'Addresses', sub: 'Warehouse address templates', to: '/admin/addresses' },
  { icon: Shield, label: 'Integrations', sub: 'SMTP and Hubtel SMS', to: '/admin/integrations' },
  { icon: Megaphone, label: 'Promotions', sub: 'Configure campaigns', to: '/admin/promotions' },
  { icon: MessageSquare, label: 'Live Chats', sub: 'Respond to customers', to: '/admin/chats' },
  { icon: BarChart2, label: 'Analytics', sub: 'Performance metrics', to: '/admin/analytics' },
  { icon: FileText, label: 'Batches', sub: 'Inspect manifests', to: '/admin/batches' },
];

const STATUS_MAP = {
  at_warehouse: { label: 'At Warehouse', icon: Warehouse, color: '#1d4ed8', bg: '#dbeafe' },
  in_transit: { label: 'In Transit', icon: Ship, color: '#7c3aed', bg: '#ede9fe' },
  arrived_ghana: { label: 'In Ghana', icon: MapPin, color: '#065f46', bg: '#d1fae5' },
  ready_for_pickup: { label: 'Ready for Pickup', icon: Package, color: '#0f766e', bg: '#ccfbf1' },
  out_for_delivery: { label: 'Out for Delivery', icon: Truck, color: '#b45309', bg: '#fef3c7' },
  delivered: { label: 'Delivered', icon: CheckCircle, color: '#166534', bg: '#dcfce7' },
};

function formatDate(dateString) {
  try {
    return new Date(dateString).toLocaleDateString('en-GH', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

export default function AdminHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    axios.get('/api/admin/analytics').then((response) => setStats(response.data)).catch(() => {});
    axios.get('/api/promotions').then((response) => setPromos(response.data)).catch(() => {});
  }, []);

  const pending = stats?.pendingPayments ?? 0;
  const unpaidItems = stats?.unpaidItems ?? 0;
  const recent = stats?.recentPayments || [];
  const breakdown = stats?.statusBreakdown || [];

  return (
    <div className="ah-page">
      <div className="ah-header">
        <div className="ah-header-row">
          <div>
            <p className="ah-header-eyebrow">Admin Panel | D2D Shipping</p>
            <p className="ah-header-title">Good day, {user?.full_name?.split(' ')[0]}</p>
          </div>
          <div className="ah-header-actions">
            <ThemeToggle />
            <button type="button" className="ah-signout mobile-only-block" onClick={() => { logout(); navigate('/'); }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {pending > 0 && (
        <div className="ah-banner-wrap">
          <Link to="/admin/transactions" className="ah-pending-banner">
            <div className="ah-pending-left">
              <Zap size={18} strokeWidth={2} className="ah-pending-icon" />
              <div>
                <p className="ah-pending-title">
                  {pending} payment{pending !== 1 ? 's' : ''} need{pending === 1 ? 's' : ''} verification
                </p>
                <p className="ah-pending-sub">Open transactions to review them now.</p>
              </div>
            </div>
            <span className="ah-pending-arrow">{'>'}</span>
          </Link>
        </div>
      )}

      {promos.length > 0 && (
        <div className="ah-banner-wrap">
          <ActivePromotionsBanner promotions={promos} audience="admin" />
        </div>
      )}

      {stats && (
        <div className="ah-stats-wrap">
          <div className="ah-stat">
            <p className="ah-stat-label">Total Revenue</p>
            <p className="ah-stat-value">
              GHS {parseFloat(stats.totalRevenue || 0).toLocaleString('en-GH', { minimumFractionDigits: 0 })}
            </p>
          </div>
          <div className="ah-stat">
            <p className="ah-stat-label">Shipments</p>
            <p className="ah-stat-value">{stats.totalShipments ?? 0}</p>
          </div>
          <div className="ah-stat">
            <p className="ah-stat-label">Customers</p>
            <p className="ah-stat-value">{stats.totalCustomers ?? 0}</p>
          </div>
          <div className={`ah-stat${unpaidItems > 0 ? ' ah-stat--alert' : ''}`}>
            <p className="ah-stat-label">Unpaid Items</p>
            <p className={`ah-stat-value${unpaidItems > 0 ? ' ah-stat-value--warn' : ''}`}>{unpaidItems}</p>
          </div>
        </div>
      )}

      <div className="ah-body">
        <div className="ah-grid">
          <div className="ah-col-left">
            {breakdown.length > 0 && (
              <>
                <p className="section-title">Shipments by Status</p>
                <div className="ah-breakdown">
                  {breakdown.map((row) => {
                    const info = STATUS_MAP[row.status] || {
                      label: row.status,
                      icon: Package,
                      color: '#374151',
                      bg: '#f3f4f6',
                    };
                    const StatusIcon = info.icon || Package;
                    return (
                      <div key={row.status} className="ah-breakdown-item" style={{ background: info.bg, borderColor: 'transparent' }}>
                        <StatusIcon size={18} strokeWidth={1.8} className="ah-breakdown-icon" style={{ color: info.color }} />
                        <div className="ah-breakdown-info">
                          <p className="ah-breakdown-count" style={{ color: info.color }}>
                            {row.count}
                          </p>
                          <p className="ah-breakdown-label">{info.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {recent.length > 0 && (
              <>
                <div className="ch-section-header" style={{ marginTop: breakdown.length ? 22 : 0 }}>
                  <p className="section-title">Recent Payments</p>
                  <Link to="/admin/transactions" className="ch-see-all">
                    See all
                  </Link>
                </div>
                <div className="ah-recent-list">
                  {recent.slice(0, 6).map((payment, index) => (
                    <div key={index} className="ah-recent-row">
                      <CreditCard size={18} strokeWidth={1.8} className="ah-recent-icon" />
                      <div className="ah-recent-info">
                        <p className="ah-recent-name">{payment.full_name}</p>
                        <p className="ah-recent-ref">
                          {payment.shipping_mark} | {formatDate(payment.created_at)}
                        </p>
                      </div>
                      <span className="ah-recent-amount">GHS {parseFloat(payment.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="ah-col-right">
            <p className="section-title">Admin Tools</p>
            <div className="ah-cards">
              {CARDS.map(({ icon: Icon, label, sub, to }) => (
                <Link key={to} to={to} className="ah-card">
                  <span className="ah-card-icon">
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <span className="ah-card-label">{label}</span>
                  <span className="ah-card-sub">{sub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
